import axios from 'axios';
import Cookies from 'js-cookie';
import { create } from 'zustand';

// cookie ids
export const SCCLIENT = 'scclientsessid';
export const SCCLIENTID = 'scclientuserid';

/**
 * @typedef {Object} UserStore
 * @property {Object} user
 * @property {Function} verifyUser
 */

/**
 * @returns {UserStore}
 */
export const useUserStore = create((set, get) => ({
	user: undefined,
	session: { id: Cookies.get(SCCLIENT), userId: Cookies.get(SCCLIENTID) }, // also created and ttl
	isSignedIn: () =>
		!!get().session?.id && !!get().session?.userId && !!get().user?.id,
	signIn: async (credentials) => {
		const { data } = await axios.post(
			'/clients/login?include=user',
			credentials,
		);
		set({ session: data, user: data.user });
		const createdAt = new Date(data.created);
		const expiresAt = new Date(createdAt.getTime() + data.ttl * 1000);
		Cookies.set(SCCLIENT, data.id, { expires: expiresAt });
		Cookies.set(SCCLIENTID, data.userId, { expires: expiresAt });
		axios.defaults.headers.common['Authorization'] = data.id;
	},
	// Exchange a single-use access token (from an emailed link) for a session.
	// Mirrors signIn's cookie/header setup; the response carries no user, so the
	// user object is loaded lazily via verifyUser when needed.
	tokenLogin: async (accessToken) => {
		const { data } = await axios.post('/clients/token-login', { accessToken });
		set({ session: data });
		const createdAt = new Date(data.created);
		const expiresAt = new Date(createdAt.getTime() + data.ttl * 1000);
		Cookies.set(SCCLIENT, data.id, { expires: expiresAt });
		Cookies.set(SCCLIENTID, data.userId, { expires: expiresAt });
		axios.defaults.headers.common['Authorization'] = data.id;
	},
	verifyUser: async () => {
		const token = Cookies.get(SCCLIENT);
		const userId = Cookies.get(SCCLIENTID);
		const user = get().user;
		if (token && userId && !user) {
			try {
				axios.defaults.headers.common['Authorization'] = token;
				const { data: userData } = await axios.get(`/clients/${userId}`);
				set({
					user: { ...userData },
				});
			} catch (err) {
				// fall through, old token, etc.
				set({ user: undefined });
				delete axios.defaults.headers.common['Authorization'];
			}
		}
	},
	updateUser: (updatedUser) => {
		set({ user: updatedUser });
	},
	signInWithoutCreds: async (username, security_answers) => {
		const { data } = await axios.post(`/clients/security-answers`, {
			username,
			security_answers,
		});
		set({
			session: { ...data, id: data.access_token, userId: data.client_id },
		});
		const createdAt = new Date(data.created);
		const expiresAt = new Date(createdAt.getTime() + data.ttl * 1000);
		Cookies.set(SCCLIENT, data.access_token, { expires: expiresAt });
		Cookies.set(SCCLIENTID, data.client_id, { expires: expiresAt });
		axios.defaults.headers.common['Authorization'] = data.id;
	},
	signOut: () => {
		delete axios.defaults.headers.common['Authorization'];
		Cookies.remove(SCCLIENT);
		Cookies.remove(SCCLIENTID);
		set({ session: undefined, user: undefined });
	},
}));
